import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ClockIcon, MapPinIcon, StopCircleIcon, PlayCircleIcon, BuildingsIcon } from "@phosphor-icons/react";
import { fmtTime } from "@/lib/tz";

/**
 * Punch-in / punch-out card for city hours (local trips).
 * Punch-in asks for start location, punch-out asks for trip number + end location.
 */
export default function DriverCityShiftCard() {
  const [active, setActive] = useState(null); // {} => none, {id: ...} => active
  const [history, setHistory] = useState([]);
  const [tick, setTick] = useState(0);
  const [inOpen, setInOpen] = useState(false);
  const [outOpen, setOutOpen] = useState(false);
  const [inForm, setInForm] = useState({ location: "", notes: "" });
  const [outForm, setOutForm] = useState({ trip_number: "", location: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [{ data: a }, { data: h }] = await Promise.all([
        api.get("/driver/city-shifts/active"),
        api.get("/driver/city-shifts?limit=10"),
      ]);
      setActive(a && a.id ? a : null);
      setHistory(h || []);
    } catch (e) {
      /* silent — card just won't show if API missing */
    }
  };

  useEffect(() => { load(); }, []);

  // live tick for the running-shift clock
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [active]);

  const capturePosition = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve({});
      navigator.geolocation.getCurrentPosition(
        (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => resolve({}),
        { timeout: 3500, maximumAge: 30000, enableHighAccuracy: false }
      );
    });

  const submitPunchIn = async (e) => {
    e.preventDefault();
    if (!inForm.location.trim()) return toast.error("Please enter a starting location");
    setBusy(true);
    try {
      const geo = await capturePosition();
      const { data } = await api.post("/driver/city-shifts/punch-in", {
        location: inForm.location.trim(),
        notes: inForm.notes,
        ...geo,
      });
      setActive(data);
      setInOpen(false);
      setInForm({ location: "", notes: "" });
      toast.success("Punched in — shift running");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to punch in");
    } finally {
      setBusy(false);
    }
  };

  const submitPunchOut = async (e) => {
    e.preventDefault();
    if (!outForm.trip_number.trim()) return toast.error("Please enter the trip number");
    if (!outForm.location.trim()) return toast.error("Please enter the ending location");
    setBusy(true);
    try {
      const geo = await capturePosition();
      await api.post("/driver/city-shifts/punch-out", {
        trip_number: outForm.trip_number.trim(),
        location: outForm.location.trim(),
        notes: outForm.notes,
        ...geo,
      });
      setActive(null);
      setOutOpen(false);
      setOutForm({ trip_number: "", location: "", notes: "" });
      toast.success("Punched out — shift saved");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to punch out");
    } finally {
      setBusy(false);
    }
  };

  // live duration display
  let liveDuration = "";
  if (active?.start_at) {
    const start = new Date(active.start_at).getTime();
    const diff = Math.max(0, Date.now() - start);
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    liveDuration = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    // eslint-disable-next-line no-unused-expressions
    tick; // keep the interval bound
  }

  return (
    <div className="p-5 bg-white border border-slate-300 rounded-sm" data-testid="driver-city-shift-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <BuildingsIcon size={18} weight="bold" className="text-blue-600" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-blue-600 font-bold">/// City Hours</div>
            <div className="font-display text-xl font-black tracking-tighter">Punch in / out</div>
          </div>
        </div>
        {active ? (
          <div className="text-right">
            <div className="font-mono text-[10px] uppercase font-bold text-emerald-700">Shift running</div>
            <div className="font-mono text-2xl font-black tabular-nums text-emerald-700" data-testid="city-shift-clock">{liveDuration}</div>
          </div>
        ) : (
          <div className="text-right">
            <div className="font-mono text-[10px] uppercase font-bold text-slate-500">Off duty</div>
          </div>
        )}
      </div>

      {active ? (
        <div className="space-y-3">
          <div className="text-xs font-mono text-slate-700 flex items-center gap-1.5">
            <MapPinIcon size={12} weight="bold" className="text-slate-500" />
            <span className="text-slate-500">Started at</span>{" "}
            <span className="font-bold">{active.start_location}</span>
            <span className="text-slate-400">·</span>
            <span className="text-slate-500">{fmtTime(active.start_at)} local</span>
          </div>
          <Button
            onClick={() => setOutOpen(true)}
            className="w-full bg-red-600 hover:bg-red-700 text-white rounded-sm font-bold h-11"
            data-testid="city-shift-punch-out-btn"
          >
            <StopCircleIcon size={18} weight="bold" className="mr-2" /> Punch out
          </Button>
        </div>
      ) : (
        <Button
          onClick={() => setInOpen(true)}
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold h-11"
          data-testid="city-shift-punch-in-btn"
        >
          <PlayCircleIcon size={18} weight="bold" className="mr-2" /> Punch in
        </Button>
      )}

      {history.length > 0 && (
        <div className="mt-4 border-t border-slate-200 pt-3">
          <div className="text-[10px] uppercase font-mono text-slate-500 font-bold mb-2 flex items-center gap-1.5">
            <ClockIcon size={11} weight="bold" /> Recent shifts
          </div>
          <div className="space-y-1.5 max-h-40 overflow-y-auto">
            {history.slice(0, 6).map((s) => (
              <div key={s.id} className="text-xs font-mono flex items-center gap-2 py-1" data-testid={`city-shift-history-${s.id}`}>
                <span className="text-slate-500 w-20">{(s.start_at || "").slice(0, 10)}</span>
                <span className="font-bold text-slate-700 w-24 truncate">{s.trip_number || "—"}</span>
                <span className="text-slate-500 truncate flex-1">{s.start_location} → {s.end_location || "…"}</span>
                <span className="tabular-nums font-bold text-blue-700">{(s.total_hours || 0).toFixed(2)}h</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Punch-in dialog */}
      <Dialog open={inOpen} onOpenChange={setInOpen}>
        <DialogContent className="max-w-md rounded-sm" data-testid="city-shift-punch-in-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Punch in — start city shift</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitPunchIn} className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Starting location *</Label>
              <Input
                autoFocus
                required
                value={inForm.location}
                onChange={(e) => setInForm({ ...inForm, location: e.target.value })}
                placeholder="e.g. Yard - Brampton ON"
                className="mt-1.5 rounded-sm"
                data-testid="city-shift-in-location"
              />
              <p className="text-[11px] text-slate-500 mt-1">Where are you starting your day? (yard, warehouse, home terminal…)</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Notes (optional)</Label>
              <Textarea
                rows={2}
                value={inForm.notes}
                onChange={(e) => setInForm({ ...inForm, notes: e.target.value })}
                className="mt-1.5 rounded-sm"
                data-testid="city-shift-in-notes"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setInOpen(false)} className="rounded-sm">Cancel</Button>
              <Button type="submit" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold" data-testid="city-shift-in-submit">
                {busy ? "Punching in…" : "Punch in"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Punch-out dialog */}
      <Dialog open={outOpen} onOpenChange={setOutOpen}>
        <DialogContent className="max-w-md rounded-sm" data-testid="city-shift-punch-out-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Punch out — end city shift</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitPunchOut} className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Trip number *</Label>
              <Input
                autoFocus
                required
                value={outForm.trip_number}
                onChange={(e) => setOutForm({ ...outForm, trip_number: e.target.value })}
                placeholder="e.g. C-4821"
                className="mt-1.5 rounded-sm font-mono"
                data-testid="city-shift-out-trip"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Ending location *</Label>
              <Input
                required
                value={outForm.location}
                onChange={(e) => setOutForm({ ...outForm, location: e.target.value })}
                placeholder="e.g. Warehouse - Toronto ON"
                className="mt-1.5 rounded-sm"
                data-testid="city-shift-out-location"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Notes (optional)</Label>
              <Textarea
                rows={2}
                value={outForm.notes}
                onChange={(e) => setOutForm({ ...outForm, notes: e.target.value })}
                className="mt-1.5 rounded-sm"
                data-testid="city-shift-out-notes"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOutOpen(false)} className="rounded-sm">Cancel</Button>
              <Button type="submit" disabled={busy} className="bg-red-600 hover:bg-red-700 text-white rounded-sm font-bold" data-testid="city-shift-out-submit">
                {busy ? "Punching out…" : "Punch out"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
